/**
 * Runtime validators for SSE event payloads that don't have shared parsers.
 *
 * File status events are NOT in the OpenAPI spec, so we validate them here.
 *
 * Every event on this stream has the same shape at the boundary: a JSON object,
 * a status the app knows, and some optional text that is either a string or
 * nothing. That shape is read once here, so a change to the boundary rule (an
 * added guard, a different null policy) is a change in one place rather than in
 * one copy per event.
 */

import type {
  ExtractionStatus,
  FileProcessingStatus,
  InventionExtractionEvent,
  NoveltyAnalysisEvent,
  FrameworkProposalEvent,
  ProposalEvent,
  ProposalStatus,
  SuggestionEvent,
  SuggestionStatus,
  ReadStatus,
} from "@patent-studio/api-types";

import { FILE_STATUS_VARIANT } from "./constants";

/** The payload as an object, or null if it is not JSON or not an object. */
function asObject(data: string): Record<string, unknown> | null {
  try {
    const parsed: unknown = JSON.parse(data);
    if (typeof parsed !== "object" || parsed === null) return null;
    return parsed as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** Optional text: a string is itself, and anything else is the absence of one. */
function textOrNull(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

/**
 * The status, if the app knows it, or null.
 *
 * Membership is read off the map keyed by the generated status type, so a state
 * added to the enum fails to compile there instead of being dropped here, which
 * would leave the page on the last state it was told about.
 */
function knownStatus<Status extends string>(
  value: unknown,
  known: Record<Status, unknown>,
): Status | null {
  if (typeof value !== "string" || !Object.hasOwn(known, value)) return null;
  return value as Status;
}

// ── File Status SSE Events ───────────────────────────────────────────────────

export interface FileStatusEvent {
  file_id: string;
  processing_status: FileProcessingStatus;
  processing_error: string | null;
  page_count: number | null;
  /** Absent from an event that says nothing of the read, so the row keeps the one it has. */
  read_status?: ReadStatus;
  unread_pages?: number[] | null;
}

/** Every read status the boundary knows, keyed by the generated type so a new one fails to compile here. */
const READ_STATUSES: Record<ReadStatus, true> = {
  reading: true,
  read: true,
  read_ocr: true,
  partly_readable: true,
  not_read: true,
  drawing: true,
};

/**
 * Parse a raw SSE data string into a typed FileStatusEvent, or null if invalid.
 *
 * The statuses let through are read off the exhaustive variant map rather than
 * listed again. A status the boundary drops never reaches the cache, so the screen
 * watching that file never moves and nothing reports an error; a status added to
 * the app without being added there is exactly how the drawing statuses were lost.
 */
export function parseFileStatusEvent(data: string): FileStatusEvent | null {
  const obj = asObject(data);
  if (obj === null) return null;
  const status = knownStatus<FileProcessingStatus>(
    obj.processing_status,
    FILE_STATUS_VARIANT,
  );
  if (status === null || typeof obj.file_id !== "string") return null;
  const event: FileStatusEvent = {
    file_id: obj.file_id,
    processing_status: status,
    processing_error: textOrNull(obj.processing_error),
    page_count: typeof obj.page_count === "number" ? obj.page_count : null,
  };
  const read = knownStatus<ReadStatus>(obj.read_status, READ_STATUSES);
  if (read === null) return event;
  const pages = obj.unread_pages;
  return {
    ...event,
    read_status: read,
    unread_pages:
      Array.isArray(pages) && pages.every((p) => typeof p === "number")
        ? pages
        : null,
  };
}

// ── Invention Extraction SSE Events ─────────────────────────────────────────

/** Every extraction status the boundary knows, and whether the pass has stopped. */
const EXTRACTION_SETTLED: Record<ExtractionStatus, boolean> = {
  not_started: false,
  running: false,
  ready: true,
  failed: true,
};

export type InventionExtractionEventShape = InventionExtractionEvent;

/** True once the pass has stopped, which is when the elements are worth asking for. */
export function extractionHasSettled(status: ExtractionStatus): boolean {
  return EXTRACTION_SETTLED[status];
}

/** Parse a raw SSE data string into a typed InventionExtractionEvent, or null if invalid. */
export function parseInventionExtractionEvent(
  data: string,
): InventionExtractionEvent | null {
  const obj = asObject(data);
  if (obj === null) return null;
  const status = knownStatus<ExtractionStatus>(obj.status, EXTRACTION_SETTLED);
  if (status === null) return null;
  return { status, error: textOrNull(obj.error) };
}

// ── Novelty Analysis SSE Events ─────────────────────────────────────────────

const PROGRESS_COUNTS = ["done", "total", "positions", "references"] as const;

/** The run's progress in cells, and the positions and references it covers. */
function isProgress(value: unknown): value is NoveltyAnalysisEvent["progress"] {
  if (typeof value !== "object" || value === null) return false;
  const counts = value as Record<string, unknown>;
  return PROGRESS_COUNTS.every((name) => typeof counts[name] === "number");
}

/**
 * Parse a raw SSE data string into a typed NoveltyAnalysisEvent, or null if invalid.
 *
 * The same status vocabulary as the extraction event, read off the same map.
 */
export function parseNoveltyAnalysisEvent(
  data: string,
): NoveltyAnalysisEvent | null {
  const obj = asObject(data);
  if (obj === null) return null;
  const status = knownStatus<ExtractionStatus>(obj.status, EXTRACTION_SETTLED);
  if (status === null || !isProgress(obj.progress)) return null;
  return {
    status,
    progress: {
      done: obj.progress.done,
      total: obj.progress.total,
      positions: obj.progress.positions,
      references: obj.progress.references,
    },
    error: textOrNull(obj.error),
  };
}

// ── Position Suggestion SSE Events ──────────────────────────────────────────

/**
 * Every status the suggestion run can report, and whether no run is in flight. A run
 * reset to not_started (a publish that failed) has stopped as surely as one that
 * completed, and the list is worth asking for again either way.
 */
const SUGGESTION_SETTLED: Record<SuggestionStatus, boolean> = {
  not_started: true,
  running: false,
  complete: true,
  failed: true,
};

/** True when no run is in flight, which is when the set is worth asking for again. */
export function suggestionHasSettled(status: SuggestionStatus): boolean {
  return SUGGESTION_SETTLED[status];
}

function stringList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];
}

/** Parse a raw SSE data string into a typed SuggestionEvent, or null if invalid. */
export function parseSuggestionEvent(data: string): SuggestionEvent | null {
  const obj = asObject(data);
  if (obj === null) return null;
  const status = knownStatus<SuggestionStatus>(obj.status, SUGGESTION_SETTLED);
  if (status === null) return null;
  return {
    status,
    run_id: textOrNull(obj.run_id),
    added_position_ids: stringList(obj.added_position_ids),
    skipped: typeof obj.skipped === "number" ? obj.skipped : 0,
    error: textOrNull(obj.error),
  };
}

// ── Claim Set Proposal SSE Events ──────────────────────────────────────────

/** Every status the proposal run can report, and whether no run is in flight. */
const PROPOSAL_SETTLED: Record<ProposalStatus, boolean> = {
  not_started: true,
  running: false,
  complete: true,
  failed: true,
};

/** True when no run is in flight, which is when the stage is worth reading again. */
export function proposalHasSettled(status: ProposalStatus): boolean {
  return PROPOSAL_SETTLED[status];
}

/** Parse a raw SSE data string into a typed ProposalEvent, or null if invalid. */
export function parseProposalEvent(data: string): ProposalEvent | null {
  const obj = asObject(data);
  if (obj === null) return null;
  const status = knownStatus<ProposalStatus>(obj.status, PROPOSAL_SETTLED);
  if (status === null) return null;
  return {
    run_id: textOrNull(obj.run_id),
    status,
    placed: typeof obj.placed === "number" ? obj.placed : 0,
    skipped: typeof obj.skipped === "number" ? obj.skipped : 0,
    error: textOrNull(obj.error),
  };
}

/** Parse a raw SSE data string into a typed FrameworkProposalEvent, or null if invalid. */
export function parseFrameworkProposalEvent(
  data: string,
): FrameworkProposalEvent | null {
  const obj = asObject(data);
  if (obj === null) return null;
  const status = knownStatus<ProposalStatus>(obj.status, PROPOSAL_SETTLED);
  if (status === null) return null;
  return {
    run_id: textOrNull(obj.run_id),
    status,
    placed: typeof obj.placed === "number" ? obj.placed : 0,
    error: textOrNull(obj.error),
  };
}
